import { GltfReferenceOutOfBoundsError, InvalidGltfManifestError } from '@/plugins/mesh/errors'
import {
  getGltfComponentByteSize,
  getGltfComponentCount,
  isGltfAccessorType,
  isGltfComponentType,
} from '@/plugins/mesh/loaders/gltfLowMemory/accessorLayout'
import type {
  GltfAccessor,
  GltfManifest,
  GltfTextureInfo,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function assertArrayField(manifest: Record<string, unknown>, field: string, source: string): void {
  const value = manifest[field]
  if (value !== undefined && !Array.isArray(value)) {
    throw new InvalidGltfManifestError(source, `${field} 必须是数组`)
  }
}

function assertNonNegativeInteger(
  value: unknown,
  reference: string,
  source: string
): asserts value is number {
  if (!Number.isInteger(value) || (value as number) < 0) {
    throw new InvalidGltfManifestError(source, `${reference} 必须是非负整数`)
  }
}

function assertIndex(
  value: unknown,
  length: number,
  reference: string,
  source: string
): asserts value is number {
  assertNonNegativeInteger(value, reference, source)
  if ((value as number) >= length) {
    throw new GltfReferenceOutOfBoundsError(source, `${reference}=${String(value)}`)
  }
}

function validateTextureInfo(
  info: GltfTextureInfo | undefined,
  texturesLength: number,
  reference: string,
  source: string
): void {
  if (!info) return
  if (!isRecord(info)) {
    throw new InvalidGltfManifestError(source, `${reference} 必须是对象`)
  }
  assertIndex(info.index, texturesLength, `${reference}.index`, source)
}

function validateAccessor(
  accessor: GltfAccessor,
  accessorIndex: number,
  manifest: GltfManifest,
  source: string
): void {
  if (!isRecord(accessor)) {
    throw new InvalidGltfManifestError(source, `accessors[${accessorIndex}] 必须是对象`)
  }
  if (!isGltfComponentType(accessor.componentType)) {
    throw new InvalidGltfManifestError(source, `accessors[${accessorIndex}].componentType 不受支持`)
  }
  if (!isGltfAccessorType(accessor.type)) {
    throw new InvalidGltfManifestError(source, `accessors[${accessorIndex}].type 无效`)
  }
  assertNonNegativeInteger(accessor.count, `accessors[${accessorIndex}].count`, source)
  const byteOffset = accessor.byteOffset ?? 0
  assertNonNegativeInteger(byteOffset, `accessors[${accessorIndex}].byteOffset`, source)

  if (accessor.bufferView === undefined) {
    if (byteOffset !== 0) {
      throw new InvalidGltfManifestError(
        source,
        `accessors[${accessorIndex}].byteOffset 在没有 bufferView 时必须为 0`
      )
    }
    return
  }

  const bufferViews = manifest.bufferViews ?? []
  assertIndex(
    accessor.bufferView,
    bufferViews.length,
    `accessors[${accessorIndex}].bufferView`,
    source
  )
  const bufferView = bufferViews[accessor.bufferView]
  const componentByteSize = getGltfComponentByteSize(accessor.componentType)
  const elementByteSize = componentByteSize * getGltfComponentCount(accessor.type)
  const byteStride = bufferView.byteStride ?? elementByteSize
  if (byteStride < elementByteSize || byteStride % componentByteSize !== 0 || byteStride > 252) {
    throw new InvalidGltfManifestError(
      source,
      `bufferViews[${accessor.bufferView}].byteStride 与 accessor 不兼容`
    )
  }
  const requiredBytes =
    accessor.count === 0
      ? byteOffset
      : byteOffset + (accessor.count - 1) * byteStride + elementByteSize
  if (requiredBytes > bufferView.byteLength) {
    throw new GltfReferenceOutOfBoundsError(
      source,
      `accessors[${accessorIndex}] 需要 ${requiredBytes} 字节，bufferView 仅有 ${bufferView.byteLength}`
    )
  }
}

function validateNodeGraph(manifest: GltfManifest, source: string): void {
  const nodes = manifest.nodes ?? []
  const states = new Uint8Array(nodes.length)
  const parents = new Int32Array(nodes.length).fill(-1)
  for (let start = 0; start < nodes.length; start += 1) {
    if (states[start] === 2) continue
    const stack: Array<{ nodeIndex: number; childOffset: number }> = [
      { nodeIndex: start, childOffset: 0 },
    ]
    while (stack.length > 0) {
      const frame = stack[stack.length - 1]
      if (states[frame.nodeIndex] === 0) states[frame.nodeIndex] = 1
      const children = nodes[frame.nodeIndex].children ?? []
      if (frame.childOffset >= children.length) {
        states[frame.nodeIndex] = 2
        stack.pop()
        continue
      }
      const childOffset = frame.childOffset
      frame.childOffset += 1
      const child = children[childOffset]
      assertIndex(child, nodes.length, `nodes[${frame.nodeIndex}].children[${childOffset}]`, source)
      if (states[child] === 1) {
        const cycle = [child]
        let current = frame.nodeIndex
        while (current !== child && current >= 0) {
          cycle.push(current)
          current = parents[current]
        }
        cycle.push(child)
        cycle.reverse()
        throw new InvalidGltfManifestError(source, `node 图存在循环: ${cycle.join(' -> ')}`)
      }
      if (states[child] === 0) {
        parents[child] = frame.nodeIndex
        stack.push({ nodeIndex: child, childOffset: 0 })
      }
    }
  }
}

function validateFiniteArray(
  value: unknown,
  length: number,
  reference: string,
  source: string
): void {
  if (value === undefined) return
  if (
    !Array.isArray(value) ||
    value.length !== length ||
    !value.every((item) => typeof item === 'number' && Number.isFinite(item))
  ) {
    throw new InvalidGltfManifestError(source, `${reference} 必须包含 ${length} 个有限数值`)
  }
}

/** 在任何大 Buffer 读取或 Geometry 分配前验证 glTF 引用和字节边界。 */
export function validateGltfManifest(value: unknown, source: string): GltfManifest {
  if (!isRecord(value)) {
    throw new InvalidGltfManifestError(source, '顶层 JSON 必须是对象')
  }
  if (!isRecord(value.asset) || typeof value.asset.version !== 'string') {
    throw new InvalidGltfManifestError(source, 'asset.version 缺失')
  }
  if (!value.asset.version.startsWith('2.')) {
    throw new InvalidGltfManifestError(source, `仅支持 glTF 2.x，收到 ${value.asset.version}`)
  }

  const arrayFields = [
    'scenes',
    'nodes',
    'meshes',
    'accessors',
    'bufferViews',
    'buffers',
    'materials',
    'textures',
    'images',
    'samplers',
    'skins',
    'animations',
    'extensionsUsed',
    'extensionsRequired',
  ]
  for (const field of arrayFields) assertArrayField(value, field, source)
  const manifest = value as unknown as GltfManifest
  const buffers = manifest.buffers ?? []
  const bufferViews = manifest.bufferViews ?? []
  const accessors = manifest.accessors ?? []
  const nodes = manifest.nodes ?? []
  const meshes = manifest.meshes ?? []
  const materials = manifest.materials ?? []
  const textures = manifest.textures ?? []
  const images = manifest.images ?? []
  const samplers = manifest.samplers ?? []
  const scenes = manifest.scenes ?? []

  buffers.forEach((buffer, index) => {
    if (!isRecord(buffer)) {
      throw new InvalidGltfManifestError(source, `buffers[${index}] 必须是对象`)
    }
    assertNonNegativeInteger(buffer.byteLength, `buffers[${index}].byteLength`, source)
    if (buffer.uri !== undefined && typeof buffer.uri !== 'string') {
      throw new InvalidGltfManifestError(source, `buffers[${index}].uri 必须是字符串`)
    }
  })

  bufferViews.forEach((bufferView, index) => {
    if (!isRecord(bufferView)) {
      throw new InvalidGltfManifestError(source, `bufferViews[${index}] 必须是对象`)
    }
    assertIndex(bufferView.buffer, buffers.length, `bufferViews[${index}].buffer`, source)
    assertNonNegativeInteger(bufferView.byteLength, `bufferViews[${index}].byteLength`, source)
    const byteOffset = bufferView.byteOffset ?? 0
    assertNonNegativeInteger(byteOffset, `bufferViews[${index}].byteOffset`, source)
    if (byteOffset + bufferView.byteLength > buffers[bufferView.buffer].byteLength) {
      throw new GltfReferenceOutOfBoundsError(
        source,
        `bufferViews[${index}] 超出 buffers[${bufferView.buffer}]`
      )
    }
    if (bufferView.byteStride !== undefined) {
      assertNonNegativeInteger(bufferView.byteStride, `bufferViews[${index}].byteStride`, source)
    }
  })

  accessors.forEach((accessor, index) => validateAccessor(accessor, index, manifest, source))

  nodes.forEach((node, index) => {
    if (!isRecord(node as unknown)) {
      throw new InvalidGltfManifestError(source, `nodes[${index}] 必须是对象`)
    }
    if (node.mesh !== undefined)
      assertIndex(node.mesh, meshes.length, `nodes[${index}].mesh`, source)
    if (node.skin !== undefined) {
      assertIndex(node.skin, manifest.skins?.length ?? 0, `nodes[${index}].skin`, source)
    }
    if (node.children !== undefined && !Array.isArray(node.children)) {
      throw new InvalidGltfManifestError(source, `nodes[${index}].children 必须是数组`)
    }
    validateFiniteArray(node.matrix, 16, `nodes[${index}].matrix`, source)
    validateFiniteArray(node.translation, 3, `nodes[${index}].translation`, source)
    validateFiniteArray(node.rotation, 4, `nodes[${index}].rotation`, source)
    validateFiniteArray(node.scale, 3, `nodes[${index}].scale`, source)
  })

  meshes.forEach((mesh, meshIndex) => {
    if (!isRecord(mesh as unknown) || !Array.isArray(mesh.primitives)) {
      throw new InvalidGltfManifestError(source, `meshes[${meshIndex}].primitives 必须是数组`)
    }
    mesh.primitives.forEach((primitive, primitiveIndex) => {
      if (!isRecord(primitive as unknown) || !isRecord(primitive.attributes as unknown)) {
        throw new InvalidGltfManifestError(
          source,
          `meshes[${meshIndex}].primitives[${primitiveIndex}].attributes 必须是对象`
        )
      }
      for (const [semantic, accessorIndex] of Object.entries(primitive.attributes)) {
        assertIndex(
          accessorIndex,
          accessors.length,
          `meshes[${meshIndex}].primitives[${primitiveIndex}].attributes.${semantic}`,
          source
        )
      }
      const attributeCounts = (Object.values(primitive.attributes) as number[]).map(
        (accessorIndex) => accessors[accessorIndex].count
      )
      if (attributeCounts.some((count) => count !== attributeCounts[0])) {
        throw new InvalidGltfManifestError(
          source,
          `meshes[${meshIndex}].primitives[${primitiveIndex}] 的 attribute accessor count 不一致`
        )
      }
      if (primitive.indices !== undefined) {
        assertIndex(
          primitive.indices,
          accessors.length,
          `meshes[${meshIndex}].primitives[${primitiveIndex}].indices`,
          source
        )
        const indexAccessor = accessors[primitive.indices]
        if (
          indexAccessor.type !== 'SCALAR' ||
          ![5121, 5123, 5125].includes(indexAccessor.componentType)
        ) {
          throw new InvalidGltfManifestError(
            source,
            `meshes[${meshIndex}].primitives[${primitiveIndex}].indices accessor 类型无效`
          )
        }
        const indexBufferView =
          indexAccessor.bufferView === undefined ? undefined : bufferViews[indexAccessor.bufferView]
        if (indexBufferView?.byteStride !== undefined) {
          throw new InvalidGltfManifestError(
            source,
            `meshes[${meshIndex}].primitives[${primitiveIndex}].indices 的 bufferView 不得定义 byteStride`
          )
        }
      }
      if (primitive.material !== undefined) {
        assertIndex(
          primitive.material,
          materials.length,
          `meshes[${meshIndex}].primitives[${primitiveIndex}].material`,
          source
        )
      }
    })
  })

  scenes.forEach((scene, sceneIndex) => {
    if (!isRecord(scene as unknown)) {
      throw new InvalidGltfManifestError(source, `scenes[${sceneIndex}] 必须是对象`)
    }
    if (scene.nodes !== undefined && !Array.isArray(scene.nodes)) {
      throw new InvalidGltfManifestError(source, `scenes[${sceneIndex}].nodes 必须是数组`)
    }
    scene.nodes?.forEach((nodeIndex, index) =>
      assertIndex(nodeIndex, nodes.length, `scenes[${sceneIndex}].nodes[${index}]`, source)
    )
  })
  if (manifest.scene !== undefined) {
    assertIndex(manifest.scene, scenes.length, 'scene', source)
  }

  textures.forEach((texture, index) => {
    if (!isRecord(texture as unknown)) {
      throw new InvalidGltfManifestError(source, `textures[${index}] 必须是对象`)
    }
    if (texture.source !== undefined) {
      assertIndex(texture.source, images.length, `textures[${index}].source`, source)
    }
    if (texture.sampler !== undefined) {
      assertIndex(texture.sampler, samplers.length, `textures[${index}].sampler`, source)
    }
  })
  images.forEach((image, index) => {
    if (!isRecord(image as unknown)) {
      throw new InvalidGltfManifestError(source, `images[${index}] 必须是对象`)
    }
    if (image.bufferView !== undefined) {
      assertIndex(image.bufferView, bufferViews.length, `images[${index}].bufferView`, source)
    }
    if (!image.uri && image.bufferView === undefined) {
      throw new InvalidGltfManifestError(source, `images[${index}] 缺少 uri 或 bufferView`)
    }
  })
  materials.forEach((material, index) => {
    if (!isRecord(material as unknown)) {
      throw new InvalidGltfManifestError(source, `materials[${index}] 必须是对象`)
    }
    validateTextureInfo(
      material.pbrMetallicRoughness?.baseColorTexture,
      textures.length,
      `materials[${index}].pbrMetallicRoughness.baseColorTexture`,
      source
    )
    validateTextureInfo(
      material.pbrMetallicRoughness?.metallicRoughnessTexture,
      textures.length,
      `materials[${index}].pbrMetallicRoughness.metallicRoughnessTexture`,
      source
    )
    validateTextureInfo(
      material.normalTexture,
      textures.length,
      `materials[${index}].normalTexture`,
      source
    )
    validateTextureInfo(
      material.occlusionTexture,
      textures.length,
      `materials[${index}].occlusionTexture`,
      source
    )
    validateTextureInfo(
      material.emissiveTexture,
      textures.length,
      `materials[${index}].emissiveTexture`,
      source
    )
  })

  validateNodeGraph(manifest, source)
  return manifest
}
